import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Globe, Plus, Eye, EyeOff, Pencil, Trash2,
  Search, X, KeyRound, Loader2, Home, ShieldCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const emptyForm = { lender_name: '', category: 'mortgage', username: '', password: '', notes: '' };

const LenderPortals = () => {
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showModalPassword, setShowModalPassword] = useState(false);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  });

  const loadPortals = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/lender-portals`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setPortals(data.portals || []);
      }
    } catch (err) {
      console.error('Failed to load portals:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPortals(); }, [loadPortals]);

  const openAdd = (category = 'mortgage') => {
    setEditingId(null);
    setForm({ ...emptyForm, category });
    setFormError('');
    setShowModalPassword(false);
    setModalOpen(true);
  };

  const openEdit = (portal) => {
    setEditingId(portal.id);
    setForm({
      lender_name: portal.lender_name,
      category: portal.category || 'mortgage',
      username: portal.username || '',
      password: portal.password || '',
      notes: portal.notes || '',
    });
    setFormError('');
    setShowModalPassword(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
  };

  const handleSubmit = async () => {
    if (!form.lender_name.trim()) { setFormError('Lender name is required.'); return; }
    setSaving(true);
    try {
      const url = editingId
        ? `${API_URL}/api/lender-portals/${editingId}`
        : `${API_URL}/api/lender-portals`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json();
        setFormError(err.detail || 'Failed to save.');
        return;
      }
      await loadPortals();
      closeModal();
    } catch {
      setFormError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_URL}/api/lender-portals/${id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      setPortals(p => p.filter(x => x.id !== id));
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const togglePassword = (id) => {
    setVisiblePasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const mortgagePortals = portals.filter(p =>
    (p.category || 'mortgage') === 'mortgage' &&
    p.lender_name.toLowerCase().includes(search.toLowerCase())
  );

  const insurancePortals = portals.filter(p =>
    p.category === 'insurance' &&
    p.lender_name.toLowerCase().includes(search.toLowerCase())
  );

  const inputClass = 'border-slate-400 dark:border-slate-500 dark:bg-slate-700 dark:text-white dark:placeholder-slate-400';

  const PortalRow = ({ portal }) => (
    <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-4 items-center px-4 py-3 border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
      {/* Lender Name */}
      <p className="font-medium text-slate-900 dark:text-white text-sm truncate">{portal.lender_name}</p>

      {/* Username */}
      <p className="text-sm text-slate-600 dark:text-slate-300 font-mono truncate">
        {portal.username || <span className="text-slate-400 dark:text-slate-500 italic font-sans">—</span>}
      </p>

      {/* Password */}
      <div className="flex items-center gap-2">
        {portal.password ? (
          <>
            <span className="text-sm text-slate-600 dark:text-slate-300 font-mono">
              {visiblePasswords[portal.id] ? portal.password : '••••••••••'}
            </span>
            <button
              onClick={() => togglePassword(portal.id)}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0"
            >
              {visiblePasswords[portal.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          </>
        ) : (
          <span className="text-slate-400 dark:text-slate-500 italic text-sm">—</span>
        )}
      </div>

      {/* Notes */}
      <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
        {portal.notes || <span className="italic">—</span>}
      </p>

      {/* Actions */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost" size="icon"
          className="h-7 w-7 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          onClick={() => openEdit(portal)}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost" size="icon"
          className="h-7 w-7 text-slate-400 hover:text-red-600 dark:hover:text-red-400"
          onClick={() => setDeleteConfirm(portal.id)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );

  const PortalSection = ({ title, icon: Icon, iconBg, iconColor, portals: list, category, accentClass }) => (
    <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
      <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${iconBg}`}>
              <Icon className={`h-4 w-4 ${iconColor}`} />
            </div>
            <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {title}
            </CardTitle>
            <span className="text-sm text-slate-400 dark:text-slate-500">({list.length})</span>
          </div>
          <Button className={`text-white text-sm ${accentClass}`} size="sm" onClick={() => openAdd(category)}>
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      </CardHeader>

      {list.length === 0 ? (
        <CardContent className="py-10 text-center">
          <Icon className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-500 dark:text-slate-400">No {category} lenders added yet.</p>
        </CardContent>
      ) : (
        <>
          {/* Column headers */}
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-4 px-4 py-2 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700">
            {['Lender', 'Username', 'Password', 'Notes', ''].map((h, i) => (
              <p key={i} className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{h}</p>
            ))}
          </div>
          <div>
            {list.map(p => <PortalRow key={p.id} portal={p} />)}
          </div>
        </>
      )}
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-red-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Lender Portals
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Saved login credentials for lender portals.</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search lenders..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={`pl-10 ${inputClass}`}
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X className="h-4 w-4 text-slate-400" />
          </button>
        )}
      </div>

      {/* Mortgage Lenders */}
      <PortalSection
        title="Mortgage Lenders"
        icon={Home}
        iconBg="bg-red-100 dark:bg-red-900/30"
        iconColor="text-red-600 dark:text-red-400"
        portals={mortgagePortals}
        category="mortgage"
        accentClass="bg-red-600 hover:bg-red-700"
      />

      {/* Insurance Lenders */}
      <PortalSection
        title="Insurance Lenders"
        icon={ShieldCheck}
        iconBg="bg-blue-100 dark:bg-blue-900/30"
        iconColor="text-blue-600 dark:text-blue-400"
        portals={insurancePortals}
        category="insurance"
        accentClass="bg-blue-600 hover:bg-blue-700"
      />

      {/* Add / Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-red-600" />
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  {editingId ? 'Edit Portal' : 'Add New Lender Portal'}
                </h2>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Category Toggle */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Category</label>
                <div className="flex rounded-lg border border-slate-400 dark:border-slate-500 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, category: 'mortgage' }))}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors ${
                      form.category === 'mortgage'
                        ? 'bg-red-600 text-white'
                        : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600'
                    }`}
                  >
                    <Home className="h-4 w-4" /> Mortgage
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, category: 'insurance' }))}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors border-l border-slate-400 dark:border-slate-500 ${
                      form.category === 'insurance'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600'
                    }`}
                  >
                    <ShieldCheck className="h-4 w-4" /> Insurance
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Lender Name <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g. Halifax, Nationwide, Santander"
                  value={form.lender_name}
                  onChange={e => setForm(f => ({ ...f, lender_name: e.target.value }))}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Username</label>
                <Input
                  placeholder="Your username or email"
                  value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  className={inputClass}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Password</label>
                <div className="relative">
                  <Input
                    type={showModalPassword ? 'text' : 'password'}
                    placeholder="Your password"
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    className={`pr-10 ${inputClass}`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowModalPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  >
                    {showModalPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notes</label>
                <textarea
                  placeholder="e.g. 2FA on phone, account locked after 3 attempts..."
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  className="w-full rounded-md border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-400 border-slate-400 dark:border-slate-500"
                />
              </div>
              {formError && <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
              <Button variant="outline" onClick={closeModal} className="dark:border-slate-600 dark:text-slate-300" disabled={saving}>
                Cancel
              </Button>
              <Button
                className={`text-white ${form.category === 'insurance' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}`}
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? 'Save Changes' : 'Add Portal'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-sm border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Delete Portal</h2>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-slate-800 dark:text-slate-200">
                {portals.find(p => p.id === deleteConfirm)?.lender_name}
              </span>? This cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="dark:border-slate-600 dark:text-slate-300">
                Cancel
              </Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteConfirm)}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LenderPortals;
